/**
 * Attack geometry on the map: the target, the run-in, and the points an operator
 * has to see to judge a terminal dive or a ballistic release.
 *
 * Drawn so it can never be read as a mission. A planned waypoint is a filled
 * command-coloured dot with a sequence number; this is a hostile-red target
 * crosshair, an amber roll-in / release point, and a solid amber run-in line —
 * a different shape and a different palette, in its own source and its own
 * layers, exactly the separation useMissionLayer draws between a draft and an
 * uploaded route. "Fly here" and "put a warhead here" must not share a symbol.
 *
 * When wind is known and the correction is real, the aim point is drawn offset
 * from the target with a thin dashed connector labelled the correction, so the
 * operator sees the airframe is being aimed upwind of the thing it is to hit.
 *
 * Created by the screen, not a child component, for the reason recorded in
 * useCommandTargetLayer: a layer added from a child effect sits under every
 * layer a later sibling adds, and target geometry belongs on top.
 */

import { useEffect, useState, type ReactNode } from 'react';

import { bindHoverLayer } from '@/map/hoverLayer';
import { geoJsonSource, useMapController, type MapLike } from '@/map/MapCanvas';
import {
  subscribeToTargeting,
  type AttackSolution,
  type TargetPoint,
} from '@/stores/targetingStore';

export const ATTACK_SOURCE_ID = 'attack-geometry';
/** Exported: the hover affordance hit-tests this layer, and so do the tests. */
export const ATTACK_HALO_LAYER = 'attack-geometry-halo';
const ATTACK_RUN_LAYER = 'attack-geometry-run';
const ATTACK_WIND_LAYER = 'attack-geometry-wind';
const ATTACK_CORE_LAYER = 'attack-geometry-core';
const ATTACK_LABEL_LAYER = 'attack-geometry-label';

/** Hostile red for the impact/target — nothing a friendly route ever uses. */
const TARGET_COLOR = '#ff3b47';
/** Amber for the flown geometry the operator commands: roll-in and release. */
const LAUNCH_COLOR = '#ffb020';
/** The wind-corrected aim, offset from the target. */
const AIM_COLOR = '#ffffff';

const EMPTY = { type: 'FeatureCollection', features: [] as unknown[] };

type Role = 'target' | 'pushover' | 'release' | 'aim';

const ROLE_LABEL: Record<Role, string> = {
  target: 'TARGET',
  pushover: 'PUSHOVER',
  release: 'RELEASE',
  aim: 'AIM',
};

function fmtDist(m: number): string {
  const v = Math.abs(m);
  return v < 1000 ? `${Math.round(m)} m` : `${(m / 1000).toFixed(2)} km`;
}

function fmtEnergy(j: number): string {
  if (j >= 1_000_000) return `${(j / 1_000_000).toFixed(1)} MJ`;
  if (j >= 1000) return `${(j / 1000).toFixed(0)} kJ`;
  return `${Math.round(j)} J`;
}

function coordLine(lat: number, lon: number): string {
  return `${lat.toFixed(5)}, ${lon.toFixed(5)}`;
}

function pointFeature(
  role: Role,
  lat: number,
  lon: number,
  title: string,
  lines: string[],
): unknown {
  return {
    type: 'Feature',
    properties: {
      role,
      label: ROLE_LABEL[role],
      title,
      // Lines are JSON so a variable-length list survives as one string
      // property; the hover handler parses it back. Same idea as the scalar
      // props in useCommandTargetLayer, kept to one field.
      lines: JSON.stringify(lines),
    },
    geometry: { type: 'Point', coordinates: [lon, lat] },
  };
}

function lineFeature(role: 'run' | 'wind', coordinates: Array<[number, number]>): unknown {
  return {
    type: 'Feature',
    properties: { role },
    geometry: { type: 'LineString', coordinates },
  };
}

/**
 * The features for the current target and solution. With a target but no
 * solution the bare crosshair is drawn, so placing a target is visible before
 * anything is computed.
 */
export function attackFeatures(
  target: TargetPoint | null,
  solution: AttackSolution | null,
): unknown[] {
  if (!target) return [];

  if (!solution) {
    return [pointFeature('target', target.lat, target.lon, 'TARGET', [coordLine(target.lat, target.lon)])];
  }

  const features: unknown[] = [];

  if (solution.kind === 'dive') {
    const d = solution.data;
    // The flown roll-in: from the pushover point down to the aim point.
    features.push(
      lineFeature('run', [
        [d.pushoverLon, d.pushoverLat],
        [d.aimLon, d.aimLat],
      ]),
    );
    // Aim is offset from the target only when wind was given and the shift is
    // real; a sub-metre correction is not worth a second marker on the ground.
    const corrected = solution.windKnown && Math.abs(d.windCorrectionM) >= 1;
    if (corrected) {
      features.push(
        lineFeature('wind', [
          [d.aimLon, d.aimLat],
          [d.targetLon, d.targetLat],
        ]),
      );
      features.push(
        pointFeature('aim', d.aimLat, d.aimLon, 'AIM · WIND-CORRECTED', [
          coordLine(d.aimLat, d.aimLon),
          `SHIFT ${fmtDist(d.windCorrectionM)} upwind`,
        ]),
      );
    }
    features.push(
      pointFeature('pushover', d.pushoverLat, d.pushoverLon, 'PUSHOVER', [
        coordLine(d.pushoverLat, d.pushoverLon),
        `ALT ${Math.round(d.pushoverAltM)} m`,
        `DIVE ${Math.round(d.diveAngleDeg)}° · BRG ${String(Math.round(d.runBearingDeg)).padStart(3, '0')}°`,
      ]),
    );
    features.push(
      pointFeature('target', d.targetLat, d.targetLon, 'IMPACT', [
        coordLine(d.targetLat, d.targetLon),
        `ENERGY ${fmtEnergy(d.impactEnergyJ)}`,
        `IMPACT ${Math.round(d.impactSpeedMs)} m/s`,
      ]),
    );
    return features;
  }

  const r = solution.data;
  // The run-in is up-track of the target by the forward throw: release here,
  // the store carries on and falls onto the target.
  features.push(
    lineFeature('run', [
      [r.releaseLon, r.releaseLat],
      [r.targetLon, r.targetLat],
    ]),
  );
  features.push(
    pointFeature('release', r.releaseLat, r.releaseLon, 'RELEASE', [
      coordLine(r.releaseLat, r.releaseLon),
      `FORWARD THROW ${fmtDist(r.forwardThrowM)}`,
      `ALT ${Math.round(r.releaseAltM)} m · BRG ${String(Math.round(r.runBearingDeg)).padStart(3, '0')}°`,
    ]),
  );
  features.push(
    pointFeature('target', r.targetLat, r.targetLon, 'IMPACT', [
      coordLine(r.targetLat, r.targetLon),
      `ENERGY ${fmtEnergy(r.impactEnergyJ)}`,
      `IMPACT ${Math.round(r.impactSpeedMs)} m/s`,
    ]),
  );
  return features;
}

/** The whole hovered point, as the label reads it out. */
export interface AttackHover {
  x: number;
  y: number;
  title: string;
  lines: string[];
  color: string;
}

interface HoverEvent {
  features?: Array<{ properties: Record<string, unknown> | null } | undefined>;
  point: { x: number; y: number };
}

const ROLE_HOVER_COLOR: Record<string, string> = {
  target: TARGET_COLOR,
  pushover: LAUNCH_COLOR,
  release: LAUNCH_COLOR,
  aim: AIM_COLOR,
};

export function hoverFromProperties(
  properties: Record<string, unknown>,
  point: { x: number; y: number },
): AttackHover | null {
  const role = properties.role;
  const title = properties.title;
  if (typeof role !== 'string' || typeof title !== 'string') return null;
  let lines: string[] = [];
  if (typeof properties.lines === 'string') {
    try {
      const parsed = JSON.parse(properties.lines) as unknown;
      if (Array.isArray(parsed)) lines = parsed.filter((l): l is string => typeof l === 'string');
    } catch {
      /* a malformed label is dropped, never thrown */
    }
  }
  return { x: point.x, y: point.y, title, lines, color: ROLE_HOVER_COLOR[role] ?? TARGET_COLOR };
}

export interface AttackLayerOptions {
  onHover?: (hover: AttackHover | null) => void;
}

export interface AttackLayer {
  destroy: () => void;
  stats: () => { redraws: number; features: number };
}

export function createAttackLayer(map: MapLike, options: AttackLayerOptions = {}): AttackLayer {
  let redraws = 0;
  let drawn = 0;

  if (!map.getSource(ATTACK_SOURCE_ID)) {
    map.addSource(ATTACK_SOURCE_ID, { type: 'geojson', data: EMPTY });
  }

  // The run-in is solid amber; the wind connector is a thin dashed white line.
  // Two layers rather than one because `line-dasharray` is not data-driven in
  // MapLibre — the same constraint the zone layer works around.
  if (!map.getLayer(ATTACK_RUN_LAYER)) {
    map.addLayer({
      id: ATTACK_RUN_LAYER,
      type: 'line',
      source: ATTACK_SOURCE_ID,
      filter: ['all', ['==', ['geometry-type'], 'LineString'], ['==', ['get', 'role'], 'run']],
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: { 'line-color': LAUNCH_COLOR, 'line-width': 2.5, 'line-opacity': 0.95 },
    });
  }

  if (!map.getLayer(ATTACK_WIND_LAYER)) {
    map.addLayer({
      id: ATTACK_WIND_LAYER,
      type: 'line',
      source: ATTACK_SOURCE_ID,
      filter: ['all', ['==', ['geometry-type'], 'LineString'], ['==', ['get', 'role'], 'wind']],
      paint: {
        'line-color': AIM_COLOR,
        'line-width': 1.3,
        'line-opacity': 0.85,
        'line-dasharray': [2, 2],
      },
    });
  }

  // Hollow rings, wider than any mission dot, hostile-red at the target and
  // amber at the flown points. The target is largest so the impact reads first.
  if (!map.getLayer(ATTACK_HALO_LAYER)) {
    map.addLayer({
      id: ATTACK_HALO_LAYER,
      type: 'circle',
      source: ATTACK_SOURCE_ID,
      filter: ['==', ['geometry-type'], 'Point'],
      paint: {
        'circle-radius': ['match', ['get', 'role'], 'target', 13, 'aim', 8, 10],
        'circle-color': 'rgba(0,0,0,0.30)',
        'circle-stroke-color': [
          'match',
          ['get', 'role'],
          'target',
          TARGET_COLOR,
          'aim',
          AIM_COLOR,
          LAUNCH_COLOR,
        ],
        'circle-stroke-width': ['match', ['get', 'role'], 'target', 2.5, 2],
      },
    });
  }

  if (!map.getLayer(ATTACK_CORE_LAYER)) {
    map.addLayer({
      id: ATTACK_CORE_LAYER,
      type: 'circle',
      source: ATTACK_SOURCE_ID,
      filter: ['==', ['geometry-type'], 'Point'],
      paint: {
        'circle-radius': ['match', ['get', 'role'], 'target', 3, 2.2],
        'circle-color': [
          'match',
          ['get', 'role'],
          'target',
          TARGET_COLOR,
          'aim',
          AIM_COLOR,
          LAUNCH_COLOR,
        ],
      },
    });
  }

  if (!map.getLayer(ATTACK_LABEL_LAYER)) {
    map.addLayer({
      id: ATTACK_LABEL_LAYER,
      type: 'symbol',
      source: ATTACK_SOURCE_ID,
      filter: ['==', ['geometry-type'], 'Point'],
      layout: {
        'text-field': ['get', 'label'],
        'text-size': 10,
        'text-letter-spacing': 0.08,
        'text-allow-overlap': true,
        // Above the marker; mission sequence numbers also sit above theirs, but
        // these are words in a hostile palette, so the two never read as one.
        'text-offset': [0, -1.6],
      },
      paint: {
        'text-color': [
          'match',
          ['get', 'role'],
          'target',
          TARGET_COLOR,
          'aim',
          AIM_COLOR,
          LAUNCH_COLOR,
        ],
        'text-halo-color': 'rgba(0,0,0,0.85)',
        'text-halo-width': 1.2,
      },
    });
  }

  let latest: { target: TargetPoint | null; solution: AttackSolution | null } = {
    target: null,
    solution: null,
  };

  function redraw(): void {
    const source = geoJsonSource(map, ATTACK_SOURCE_ID);
    if (!source) return;
    const features = attackFeatures(latest.target, latest.solution);
    source.setData({ type: 'FeatureCollection', features });
    drawn = features.length;
    redraws += 1;
  }

  const unsubscribe = subscribeToTargeting((geometry) => {
    latest = geometry;
    redraw();
  });

  const onHoverMove = (event: HoverEvent): void => {
    const properties = event.features?.[0]?.properties;
    if (!properties) return;
    options.onHover?.(hoverFromProperties(properties, event.point));
  };
  const onHoverLeave = (): void => options.onHover?.(null);

  const unbindHover = bindHoverLayer(map, ATTACK_HALO_LAYER, onHoverMove, onHoverLeave);

  return {
    destroy: () => {
      unsubscribe();
      unbindHover();
      options.onHover?.(null);
      for (const id of [
        ATTACK_LABEL_LAYER,
        ATTACK_CORE_LAYER,
        ATTACK_HALO_LAYER,
        ATTACK_WIND_LAYER,
        ATTACK_RUN_LAYER,
      ]) {
        if (map.getLayer(id)) map.removeLayer(id);
      }
      if (map.getSource(ATTACK_SOURCE_ID)) map.removeSource(ATTACK_SOURCE_ID);
    },
    stats: () => ({ redraws, features: drawn }),
  };
}

/** The hovered point, published outside React so the pointer does not re-render
 *  the planning rails at pointer rate — same split as useCommandTargetLayer. */
const hoverListeners = new Set<(hover: AttackHover | null) => void>();

function publishHover(hover: AttackHover | null): void {
  for (const listener of hoverListeners) listener(hover);
}

/** Mount the layer. Call it from the screen, alongside the other layer hooks. */
export function useAttackLayer(): void {
  const controller = useMapController();

  useEffect(
    () =>
      controller.ready((map) => {
        const layer = createAttackLayer(map, {
          onHover: (hover) => {
            const raw = controller.raw();
            if (raw) raw.getCanvas().style.cursor = hover ? 'pointer' : '';
            publishHover(hover);
          },
        });
        return () => layer.destroy();
      }),
    [controller],
  );
}

const LABEL_WIDTH = 210;

/** The hover label for whatever attack point the pointer is over. */
export function AttackHoverLabel(): ReactNode {
  const [hover, setHover] = useState<AttackHover | null>(null);

  useEffect(() => {
    hoverListeners.add(setHover);
    return () => {
      hoverListeners.delete(setHover);
    };
  }, []);

  if (!hover) return null;

  const viewportWidth = typeof window === 'undefined' ? 1280 : window.innerWidth;
  const left = Math.min(hover.x + 14, viewportWidth - LABEL_WIDTH - 12);

  return (
    <div
      role="tooltip"
      className="glass pointer-events-none absolute z-40 rounded-[12px] px-3 py-2"
      style={{ left, top: hover.y + 14, maxWidth: LABEL_WIDTH }}
    >
      <span
        className="block font-mono text-[11px] font-semibold tracking-wide"
        style={{ color: hover.color }}
      >
        {hover.title}
      </span>
      {hover.lines.map((line) => (
        <span key={line} className="mt-0.5 block font-mono text-[9.5px] text-white/55">
          {line}
        </span>
      ))}
    </div>
  );
}

export default AttackHoverLabel;
